import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { ProfileLiteExtService } from '../../shared/profile-lite-ext.service';
import { Store } from '../../shared/store';
import { WindowService } from '../../shared/window.service';

// Extends the namespace while typescript Intl types are outdated
namespace IntlExtended {
  export class NumberFormat extends Intl.NumberFormat {
    constructor(locales: string | string[], options: Intl.NumberFormatOptions & { currencyDisplay: string }) {
      super(locales, options);
    }
  }
}

interface ReferralPageBannerOptions {
  showBanner?: boolean;
  showHeaderLink?: boolean;
  isElligible?: boolean;
  localizedBonusAmount?: string;
}

const HIDE_REFERRAL_BANNER_COOKIE_NAME = 'hide_referral_banner';

@Injectable()
export class ReferralPageBannerService extends Store<ReferralPageBannerOptions> {
  constructor(
    private profileLiteExtService: ProfileLiteExtService,
    oeAppPersonService: OeAppPersonService,
    private windowService: WindowService
  ) {
    // showBanner should be null as we need to wait for the profile to be fetched before we can determine if the banner should be shown or not
    super({ isElligible: false, showBanner: null, showHeaderLink: false });

    if (oeAppPersonService.isReferralProgramEnabled) {
      this.defineIfItShouldShowTheBanner();
    } else {
      this.changeState({ isElligible: false, showBanner: false, showHeaderLink: false });
    }
  }

  dismissBanner() {
    localStorage.setItem(HIDE_REFERRAL_BANNER_COOKIE_NAME, 'true');
    this.changeState({ showBanner: false, showHeaderLink: true });
  }

  redirectToReferral() {
    this.windowService.redirectToUrl('/referrals.html');
  }

  public get showBanner$() {
    return this.state$.pipe(map((state) => state.showBanner));
  }

  public get showHeaderLink$() {
    return this.state$.pipe(map((state) => state.showHeaderLink));
  }

  public get isElligible$() {
    return this.state$.pipe(map((state) => state.isElligible));
  }

  public get localizedBonusAmount$() {
    return this.state$.pipe(map((state) => state.localizedBonusAmount));
  }

  private getReferralMetadata$(): Observable<{ amount: number; currency: string }> {
    return this.profileLiteExtService.getPersonData$()
      .pipe(
        map(({ referralAmount, referralCurrency }) => ({ amount: referralAmount, currency: referralCurrency })),
        catchError(() => of({ amount: null, currency: null }))
      );
  }

  private defineIfItShouldShowTheBanner() {
    this.getReferralMetadata$().subscribe(({ amount, currency }) => {
      if (!amount) {
        this.changeState({ isElligible: false, showBanner: false, showHeaderLink: false });
      } else {
        if (this.hideReferralBanner) {
          this.changeState({
            isElligible: true,
            showBanner: false,
            showHeaderLink: true,
            localizedBonusAmount: `${amount} ${currency}`,
          });
        } else {
          this.changeState({
            isElligible: true,
            showBanner: true,
            showHeaderLink: false,
            localizedBonusAmount: `${amount} ${currency}`,
          });
        }
      }
    });
  }

  private changeState(newState: ReferralPageBannerOptions) {
    this.setState({ ...this.state, ...newState });
  }

  private get hideReferralBanner() {
    return (
      localStorage.getItem(HIDE_REFERRAL_BANNER_COOKIE_NAME) &&
      localStorage.getItem(HIDE_REFERRAL_BANNER_COOKIE_NAME) === 'true'
    );
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/referral-page-banner/referral-page-banner.service.ts